<script setup lang="ts">
import { ref } from 'vue'
import VueEasyLightbox from 'vue-easy-lightbox'
import { Image, Play, X } from 'lucide-vue-next'

// Video type for services
type ServiceVideo = {
  url: string
  title: string
}

// Services videos
const makeupVideoUrl = 'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/services/make-up-and-hair-do.mp4'
const dressRentalVideoUrl = 'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/services/dress-rental.mp4'
const photographyVideoUrl = 'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/services/photography.mp4'

// Video Modal
const videoModalOpen = ref(false)
const currentVideo = ref<ServiceVideo | null>(null)

const openVideoModal = (url: string, title: string) => {
  currentVideo.value = { url, title }
  videoModalOpen.value = true
}

const closeVideoModal = () => {
  videoModalOpen.value = false
  // Small delay to allow transition to complete before clearing video
  setTimeout(() => {
    currentVideo.value = null
  }, 300)
}

// Instagram Gallery Lightbox
const visible = ref(false)
const index = ref(0)

// Generate image list for lightbox (WebP format for better performance)
const images = Array.from({ length: 48 }, (_, i) => ({
  src: `https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/memories/instagram-${String(i + 1).padStart(3, '0')}.webp`,
  title: `Achiraya Studio ${i + 1}`
}))

const showImage = (idx: number) => {
  index.value = idx
  visible.value = true
}

const handleHide = () => {
  visible.value = false
}
</script>

<template>
  <div class="landing-view">
    <!-- Hero Section: Editorial Masonry Grid Showcase -->
    <section class="relative overflow-x-hidden bg-white">
      <div class="max-w-[1600px] mx-auto">
        <!-- Asymmetric Masonry Grid -->
        <div class="grid grid-cols-12 grid-rows-3 gap-3 p-1">
          <!-- Left: Large Video (spans 2 rows) -->
          <div class="col-span-12 md:col-span-4 row-span-2" id="grid-top-1">
            <div class="h-full bg-gray-100 relative overflow-hidden group">
              <video
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/top-001-vdo.mp4"
                autoplay
                loop
                muted
                playsinline
                class="absolute inset-0 w-full h-full object-cover"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Top Middle: Landscape Photo -->
          <div class="col-span-6 md:col-span-4" id="grid-top-2">
            <div class="aspect-[4/3] bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero002.webp"
                alt="Thai traditional dress photoshoot"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Top Right: Actual Photo -->
          <div class="col-span-6 md:col-span-4" id="grid-top-3">
            <div class="aspect-[4/3] bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero003.webp"
                alt="Traditional Thai dress photoshoot at Wat Arun"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Middle Left: Square (aligns with video bottom) -->
          <div class="col-span-4 md:col-span-2" id="grid-mid-1">
            <div class="aspect-square bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero003.webp"
                alt="Traditional Thai dress photoshoot at Wat Arun"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Center: Wide Landscape Video (extends to bottom) -->
          <div class="col-span-8 md:col-span-4 row-span-2" id="grid-bottom-004">
            <div class="h-full bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero007.webp"
                alt="Thai traditional makeup and dress"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Right: Tall Portrait (spans rows 2-3) - extends to bottom -->
          <div class="col-span-12 md:col-span-2 row-span-2" id="grid-bottom-005">
            <div class="h-full bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero008.webp"
                alt="Thai traditional makeup and dress"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Bottom Left: Portrait Photo -->
          <div class="col-span-4 md:col-span-2 h-full" id="grid-bottom-001">
            <div class="h-full bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero004.webp"
                alt="Thai traditional dress portrait"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Bottom Middle: Portrait Photo -->
          <div class="col-span-4 md:col-span-2 h-full" id="grid-bottom-002">
            <div class="h-full bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero005.webp"
                alt="Thai traditional dress portrait"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>

          <!-- Bottom Right: Portrait Photo -->
          <div class="col-span-4 md:col-span-2 h-full" id="grid-bottom-003">
            <div class="h-full bg-gray-100 relative overflow-hidden group">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/landing/hero006.webp"
                alt="Thai traditional dress portrait"
                class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            </div>
          </div>
        </div>
      </div>

      <!-- Hero Content Overlay -->
      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div class="text-center bg-white/95 backdrop-blur-sm px-3 py-3 md:px-12 md:py-10 max-w-xl mx-4 pointer-events-auto">
          <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{{ $t('landing.hero.subtitle') }}</p>
          <h1 class="text-3xl md:text-4xl font-light text-gray-900 mb-4 leading-tight">
            {{ $t('landing.hero.title') }}
          </h1>
          <p class="text-gray-600 mb-8 text-sm leading-relaxed">
            {{ $t('landing.hero.description') }}
          </p>
          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <router-link
              to="/booking"
              class="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
            >
              {{ $t('landing.hero.book_now') }}
            </router-link>
            <router-link
              to="/packages"
              class="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-gray-900 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
            >
              {{ $t('landing.hero.view_packages') }}
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section class="py-24 bg-stone-50">
      <div class="max-w-6xl mx-auto px-6 lg:px-8">
        <div class="text-center mb-16">
          <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{{ $t('landing.services.subtitle') }}</p>
          <h2 class="text-2xl md:text-3xl font-light text-gray-900">
            {{ $t('landing.services.title') }}
          </h2>
        </div>

        <div class="grid md:grid-cols-3 gap-8 items-stretch">
          <!-- Makeup -->
          <div class="text-center group flex flex-col">
            <div class="service-items aspect-square mx-auto mb-6 relative overflow-hidden bg-gray-100 rounded-lg w-full">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/services/make-up.png"
                :alt="$t('landing.services.makeup.title')"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <h3 class="text-sm font-medium uppercase tracking-wide text-gray-900 mb-3">{{ $t('landing.services.makeup.title') }}</h3>
            <p class="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">
              {{ $t('landing.services.makeup.description') }}
            </p>
            <button
              @click="openVideoModal(makeupVideoUrl, $t('landing.services.makeup.title'))"
              class="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors rounded-full w-fit mt-auto mx-auto"
            >
              <Play class="w-4 h-4" />
              {{ $t('landing.services.watch_video') }}
            </button>
          </div>

          <!-- Dress -->
          <div class="text-center group flex flex-col">
            <div class="service-items aspect-square mx-auto mb-6 relative overflow-hidden bg-gray-100 rounded-lg w-full">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/services/dress-rental.png"
                :alt="$t('landing.services.dress.title')"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <h3 class="text-sm font-medium uppercase tracking-wide text-gray-900 mb-3">{{ $t('landing.services.dress.title') }}</h3>
            <p class="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">
              {{ $t('landing.services.dress.description') }}
            </p>
            <button
              @click="openVideoModal(dressRentalVideoUrl, $t('landing.services.dress.title'))"
              class="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors rounded-full w-fit mt-auto mx-auto"
            >
              <Play class="w-4 h-4" />
              {{ $t('landing.services.watch_video') }}
            </button>
          </div>

          <!-- Photography -->
          <div class="text-center group flex flex-col">
            <div class="service-items aspect-square mx-auto mb-6 relative overflow-hidden bg-gray-100 rounded-lg w-full">
              <img
                src="https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/services/photography.png"
                :alt="$t('landing.services.photo.title')"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <h3 class="text-sm font-medium uppercase tracking-wide text-gray-900 mb-3">{{ $t('landing.services.photo.title') }}</h3>
            <p class="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">
              {{ $t('landing.services.photo.description') }}
            </p>
            <button
              @click="openVideoModal(photographyVideoUrl, $t('landing.services.photo.title'))"
              class="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors rounded-full w-fit mt-auto mx-auto"
            >
              <Play class="w-4 h-4" />
              {{ $t('landing.services.watch_video') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gray-500">
      <div class="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <h2 class="text-2xl md:text-3xl font-light text-white mb-4">
          {{ $t('landing.cta.ready_title') }}
        </h2>
        <p class="text-gray-200 mb-10 text-sm leading-relaxed max-w-lg mx-auto">
          {{ $t('landing.cta.ready_description') }}
        </p>
        <router-link
          to="/packages"
          class="inline-flex items-center justify-center px-10 py-4 bg-white text-gray-900 text-sm font-medium tracking-wide hover:bg-gray-100 transition-colors"
        >
          {{ $t('landing.cta.select_package') }}
        </router-link>
      </div>
    </section>

    <!-- Instagram Gallery Section -->
    <section class="py-20 bg-white border-t border-gray-100">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Section Header -->
        <div class="text-center mb-12">
          <a
            href="https://instagram.com/achiraya.studio"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-3 text-gray-900 hover:text-gray-600 transition-colors"
          >
            <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <span class="text-xl md:text-2xl font-medium tracking-tight">@achiraya.studio</span>
          </a>
          <p class="text-gray-500 mt-3 text-sm">{{ $t('landing.gallery.follow_us') }}</p>
        </div>

        <!-- Instagram Grid -->
        <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1 md:gap-2">
          <div
            v-for="i in 48"
            :key="i"
            class="aspect-square relative group overflow-hidden bg-gray-200 cursor-pointer"
            @click="showImage(i - 1)"
          >
            <img
              :src="`https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/memories/instagram-${String(i).padStart(3, '0')}.webp`"
              :alt="`Achiraya Studio ${i}`"
              class="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
            />
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <Image
                class="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              />
            </div>
          </div>
        </div>

        <!-- Lightbox -->
        <VueEasyLightbox
          :visible="visible"
          :imgs="images"
          :index="index"
          @hide="handleHide"
          :moveDisabled="false"
          :zoomDisabled="false"
          :rotateDisabled="true"
          :scrollDisabled="true"
          :maskClosable="true"
          :showIndex="true"
        />

        <!-- Follow Button -->
        <div class="text-center mt-10">
          <a
            href="https://instagram.com/achiraya.studio"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            {{ $t('landing.gallery.follow_on_instagram') }}
          </a>
        </div>
      </div>
    </section>

    <!-- Quote/Atmosphere Section -->
    <section class="py-24 bg-white">
      <div class="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <blockquote class="mb-8">
          <p class="text-2xl md:text-3xl font-light text-gray-900 leading-relaxed italic">
            "{{ $t('landing.quote.text') }}"
          </p>
        </blockquote>
        <div class="w-16 h-px bg-gray-300 mx-auto mb-6" />
        <p class="text-sm text-gray-500 uppercase tracking-widest mb-8">{{ $t('landing.quote.author') }}</p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link
            to="/booking"
            class="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
          >
            {{ $t('landing.hero.book_now') }}
          </router-link>
          <router-link
            to="/packages"
            class="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-gray-900 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
          >
            {{ $t('landing.hero.view_packages') }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- Social Links Section -->
    <section class="py-20 bg-stone-50">
      <div class="max-w-4xl mx-auto px-6 lg:px-8">
        <div class="text-center mb-12">
          <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{{ $t('landing.social.subtitle') }}</p>
          <h2 class="text-2xl md:text-3xl font-light text-gray-900">
            {{ $t('landing.social.title') }}
          </h2>
        </div>

        <!-- Social Links Grid -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          <!-- Instagram -->
          <a
            href="https://instagram.com/achiraya.studio"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:border-pink-200 hover:shadow-lg hover:shadow-pink-100/50 transition-all duration-300"
          >
            <div class="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-pink-600 transition-colors">{{ $t('landing.social.instagram') }}</span>
          </a>

          <!-- Facebook -->
          <a
            href="https://www.facebook.com/profile.php?id=61565649761726&mibextid=LQQJ4d&mibextid=LQQJ4d"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300"
          >
            <div class="w-14 h-14 flex items-center justify-center rounded-full bg-blue-600 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">{{ $t('landing.social.facebook') }}</span>
          </a>

          <!-- TikTok -->
          <a
            href="https://tiktok.com/@achiraya.studio"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:border-gray-800 hover:shadow-lg hover:shadow-gray-200/50 transition-all duration-300"
          >
            <div class="w-14 h-14 flex items-center justify-center rounded-full bg-black group-hover:scale-110 transition-transform duration-300">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">{{ $t('landing.social.tiktok') }}</span>
          </a>

          <!-- WhatsApp -->
          <a
            href="https://api.whatsapp.com/send?phone=0952705676"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-300"
          >
            <div class="w-14 h-14 flex items-center justify-center rounded-full bg-green-500 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors">{{ $t('landing.social.whatsapp') }}</span>
          </a>

          <!-- LINE Official -->
          <a
            href="https://lin.ee/QpmIqtS"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:border-green-300 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-300"
          >
            <div class="w-14 h-14 flex items-center justify-center rounded-full bg-[#06C755] group-hover:scale-110 transition-transform duration-300">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-[#06C755] transition-colors">{{ $t('landing.social.line') }}</span>
          </a>

          <!-- Xiaohongshu (小红书) -->
          <a
            href="https://www.xiaohongshu.com/user/profile/66cf299d000000001d022f85?xhsshare=CopyLink&appuid=6592efbc000000002200991f&apptime=1730622813&share_id=27e12d85c824460ab04086e47cea0f98"
            target="_blank"
            rel="noopener noreferrer"
            class="group flex flex-col items-center gap-3 p-6 bg-white rounded-xl border border-gray-100 hover:border-red-200 hover:shadow-lg hover:shadow-red-100/50 transition-all duration-300"
          >
            <div class="w-14 h-14 flex items-center justify-center rounded-full bg-red-500 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.615 14.615h-2.77v-3.077h-3.69v3.077H7.385V7.385h2.77v3.077h3.69V7.385h2.77v9.23z"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-gray-700 group-hover:text-red-500 transition-colors">{{ $t('landing.social.xiaohongshu') }}</span>
          </a>
        </div>

        <!-- Contact Info -->
        <div class="mt-12 text-center">
          <div class="inline-flex flex-col items-center gap-4 text-gray-600">
            <!-- Phone Numbers -->
            <div class="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
              <a href="tel:+66830131218" class="flex items-center gap-2 hover:text-gray-900 transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span class="text-sm">{{ $t('landing.contact.phone') }}</span>
              </a>
              <span class="hidden sm:block w-px h-4 bg-gray-300"></span>
              <a href="tel:+66924988850" class="flex items-center gap-2 hover:text-gray-900 transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span class="text-sm">{{ $t('landing.contact.phone2') }}</span>
              </a>
            </div>
            <!-- Location -->
            <div class="flex items-center gap-2 pt-2 w-full justify-center">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span class="text-sm">{{ $t('landing.contact.location') }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gray-900">
      <div class="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <h2 class="text-2xl md:text-3xl font-light text-white mb-4">
          {{ $t('landing.cta.photoshoot_title') }}
        </h2>
        <p class="text-gray-400 mb-10 text-sm leading-relaxed max-w-lg mx-auto">
          {{ $t('landing.cta.photoshoot_description') }}
        </p>
        <router-link
          to="/booking"
          class="inline-flex items-center justify-center px-10 py-4 bg-white text-gray-900 text-sm font-medium tracking-wide hover:bg-gray-100 transition-colors"
        >
          {{ $t('landing.cta.start_booking') }}
        </router-link>
      </div>
    </section>

    <!-- Video Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="videoModalOpen"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          @click="closeVideoModal"
        >
          <div
            class="relative mx-4 bg-black rounded-lg overflow-hidden"
            @click.stop
          >
            <!-- Close Button -->
            <button
              @click="closeVideoModal"
              class="fixed top-[10px] right-[10px] z-[100] w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            >
              <X class="w-5 h-5 text-white" />
            </button>

            <!-- Video (9:16 Portrait) - Loaded on demand -->
            <video
              v-if="currentVideo"
              :key="currentVideo.url"
              :src="currentVideo.url"
              controls
              autoplay
              loop
              playsinline
              preload="none"
              class="w-full max-h-[80vh] aspect-[9/16] object-contain"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
/* Ensure grid fills available height */
.grid-rows-3 {
  grid-template-rows: repeat(3, minmax(0, 1fr));
}

.service-items {
  max-width: 440px;
  max-height: 440px;
}

/* Override Vue Easy Lightbox styles */
:deep(.vel-modal) {
  background: rgba(255, 255, 255, 0.8) !important;
}

:deep(.vel-img) {
  box-shadow: 0 5px 20px 2px rgba(156, 156, 156, 0.7) !important;
}

/* Custom Navigation Buttons - Override default styles */
:deep(.vel-prev-button),
:deep(.vel-next-button) {
  position: absolute !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  width: 80px !important;
  height: 80px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 50% !important;
  cursor: pointer !important;
  color: #000000 !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
  z-index: 10002 !important;
  opacity: 1 !important;
  visibility: visible !important;
}

:deep(.vel-prev-button) {
  left: 40px !important;
}

:deep(.vel-next-button) {
  right: 40px !important;
}

:deep(.vel-prev-button:hover),
:deep(.vel-next-button:hover) {
  background: #fff !important;
  transform: translateY(-50%) scale(1.1) !important;
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.3) !important;
}

/* Larger icons inside buttons */
:deep(.btn__prev),
:deep(.btn__next) {
  background-color: #fff;
  border-radius: 50%;
  width: 106px !important;
  height: 106px !important;
  padding: 20px;
}
:deep(.btn__prev) svg,
:deep(.btn__next) svg,
:deep(.btn__prev) .vel-icon,
:deep(.btn__next) .vel-icon {
  width: 66px !important;
  height: 66px !important;
  color: #494949 !important;
  fill: #494949 !important;
}

/* Ensure buttons are visible on mobile too */
@media (max-width: 768px) {
  :deep(.vel-prev-button),
  :deep(.vel-next-button) {
    width: 60px !important;
    height: 60px !important;
  }
  :deep(.vel-prev-button) {
    left: 15px !important;
  }
  :deep(.vel-next-button) {
    right: 15px !important;
  }
  :deep(.vel-prev-button) svg,
  :deep(.vel-next-button) svg {
    width: 28px !important;
    height: 28px !important;
  }
}

/* Image counter/index display */
:deep(.vel-index) {
  background: rgba(255, 255, 255, 0.9) !important;
  color: #333 !important;
  border-radius: 20px !important;
  padding: 6px 16px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Responsive hero grid adjustments */
@media (max-width: 1376px) {
  #grid-top-2,
  #grid-top-3 {
    & > div {
      width: 100%;
      height: 100%;
    }
  }
}

@media (max-width: 767px) {
  #grid-mid-1 {
    display: none;
  }
  #grid-bottom-004 {
    width: 100% !important;
    height: 426px !important;
    grid-column: span 12 / span 12 !important;
  }
  #grid-bottom-005 {
    display: none;
  }
}
</style>
